import { Button } from "@mindlercare/ui-web";
import { Typography } from "components/typography";
import { useGetAllSlotData } from "api/stats";
import { DashboardGridItem, DoubleSpanGridContentWrapper } from "./styles";
import { useState } from "react";
import { useNavigate } from "react-router-dom";

type TimeslotsDashboardItemProps = {
  psychologistUserId: number;
  fromDate: Date;
  toDate: Date;
  previousFromDate: Date;
  previousToDate: Date;
  dateRangeLengthInDays?: number;
};

type Patient = {
  userId: number;
  firstName: string | null;
  lastName: string | null;
};

export const PatientsWithoutFutureBookings = ({
  fromDate,
  toDate,
  psychologistUserId: selectedPsychologistUserId,
}: TimeslotsDashboardItemProps): JSX.Element => {
  const { data: currentData } = useGetAllSlotData({
    fromDate: fromDate,
    toDate: toDate,
    psychologistUserId: selectedPsychologistUserId,
  });
  const navigate = useNavigate();

  const [showAllFirstMeetingsPatients, setFirstAllSecondMeetingsPatients] =
    useState<boolean>(false);
  const [showAllSecondMeetingsPatients, setShowAllSecondMeetingsPatients] =
    useState<boolean>(false);

  const firstMeetingPatients = showAllFirstMeetingsPatients
    ? (currentData?.patients?.firstMeetings?.patients ?? [])
    : (currentData?.patients?.firstMeetings?.patients ?? []).slice(0, 5);
  const firstMeetingsTotal = currentData?.patients?.firstMeetings?.total ?? 0;

  const secondMeetingsPatients = showAllSecondMeetingsPatients
    ? (currentData?.patients?.secondMeetings?.patients ?? [])
    : (currentData?.patients?.secondMeetings?.patients ?? []).slice(0, 5);
  const secondMeetingsTotal = currentData?.patients?.secondMeetings?.total ?? 0;

  return (
    <DashboardGridItem doubleSpan>
      <DoubleSpanGridContentWrapper>
        {/* eslint-disable-next-line i18next/no-literal-string */}
        <Typography variant="h3">
          Long term patients without future bookings
        </Typography>
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          {/* eslint-disable-next-line i18next/no-literal-string */}
          <Typography variant="body-x-small-bold" color="GRAY_BLACK">
            Name
          </Typography>
        </div>
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            gap: "1rem",
          }}
        >
          {secondMeetingsPatients.map((patient: Patient) => {
            return (
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                }}
                key={patient.userId}
              >
                <Typography variant="body" color="GRAY_DARK">
                  {patient.firstName} {patient.lastName}
                </Typography>
                <Button
                  variant="secondary"
                  color="primary"
                  onClick={() =>
                    navigate(
                      `/patients/psychologist/${selectedPsychologistUserId}/patient/${patient.userId}/bookings`,
                    )
                  }
                >
                  {"View"}
                </Button>
              </div>
            );
          })}
          {secondMeetingsPatients.length > 5 && (
            <div style={{ alignSelf: "flex-start" }}>
              <Button
                variant="link-button"
                color="primary"
                style={{ alignSelf: "self-start" }}
                onClick={() =>
                  setShowAllSecondMeetingsPatients((prev) => !prev)
                }
              >
                {showAllSecondMeetingsPatients
                  ? "Show less"
                  : `Show all ${secondMeetingsTotal} patients`}
              </Button>
            </div>
          )}
        </div>
      </DoubleSpanGridContentWrapper>

      <DoubleSpanGridContentWrapper>
        {/* eslint-disable-next-line i18next/no-literal-string */}
        <Typography variant="h3">
          First time patients without future bookings
        </Typography>
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          {/* eslint-disable-next-line i18next/no-literal-string */}
          <Typography variant="body-x-small-bold" color="GRAY_BLACK">
            Name
          </Typography>
        </div>
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            gap: "1rem",
          }}
        >
          {firstMeetingPatients.map((patient: Patient) => {
            return (
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                }}
                key={patient.userId}
              >
                <Typography variant="body" color="GRAY_DARK">
                  {patient.firstName} {patient.lastName}
                </Typography>
                <Button
                  variant="secondary"
                  color="primary"
                  onClick={() =>
                    navigate(
                      `/patients/psychologist/${selectedPsychologistUserId}/patient/${patient.userId}/bookings`,
                    )
                  }
                >
                  {"View"}
                </Button>
              </div>
            );
          })}
          {firstMeetingPatients.length > 5 && (
            <div style={{ alignSelf: "flex-start" }}>
              <Button
                variant="link-button"
                color="primary"
                style={{ alignSelf: "self-start" }}
                onClick={() =>
                  setFirstAllSecondMeetingsPatients((prev) => !prev)
                }
              >
                {showAllFirstMeetingsPatients
                  ? "Show less"
                  : `Show all ${firstMeetingsTotal} patients`}
              </Button>
            </div>
          )}
        </div>
      </DoubleSpanGridContentWrapper>
    </DashboardGridItem>
  );
};
